import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaPlus, FaMap, FaSpinner, FaSave, FaUndo, FaEye } from 'react-icons/fa';
import { usePermission } from '../../hooks/usePermission';
import HaventPermission from '../../assets/HaventPermission';
import ObjectTree from './ObjectTree';
import SchemaEditor from './SchemaEditor';
import PropertiesPanel from './PropertiesPanel';
import { selectObjectTree } from '../../actions/infoTerminal';
import { fetchObjectsTree, fetchAvailableBuildings, addTerritory, saveAllChanges, discardChanges } from '../../actions/infoTerminalApi';
import styles from './styles.module.scss';

const InfoTerminal = ({
  objectTree,
  treeLoading,
  hasChanges,
  saving
}) => {

  const dispatch = useDispatch();
  const { updateRight } = usePermission('Настройка', 'Информационный терминал');
  
  useEffect(() => {
    // Загружаем данные с сервера при монтировании
    if (!updateRight) return
    
    dispatch(fetchObjectsTree());
    dispatch(fetchAvailableBuildings());
  }, [dispatch]);

  // Предупреждение при закрытии вкладки с несохранёнными изменениями
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = 'У вас есть несохранённые изменения. Вы уверены, что хотите покинуть страницу?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Ctrl+S для сохранения
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS') {
        e.preventDefault();
        if (hasChanges && !saving) {
          dispatch(saveAllChanges());
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasChanges, saving, dispatch]);

  if (!updateRight) return <HaventPermission />;

  const handleAddRoot = () => {
    // Добавление территории через API
    dispatch(addTerritory('Новая территория'));
  };

  const handleSave = () => {
    dispatch(saveAllChanges());
  };

  const handleDiscard = () => {
    dispatch(discardChanges());
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div className={styles.titleRow}>
          <p className={styles.title}>Конструктор схемы</p>
          <Link to="/info_terminal_view" className={styles.viewModeBtn}>
            <FaEye size={14} />
            <span>Режим просмотра</span>
          </Link>
        </div>

        <div className={styles.headerActions}>
          <button
            className={`${styles.saveBtn} ${hasChanges ? styles.hasChanges : ''}`}
            onClick={handleSave}
            disabled={saving || !hasChanges}
            title={hasChanges ? 'Сохранить изменения' : 'Нет изменений'}
          >
            {saving ? <FaSpinner className={styles.spinner} size={12} /> : <FaSave size={12} />}
            <span>{saving ? 'Сохранение...' : 'Сохранить'}</span>
          </button>
        </div>
      </div>

      <div className={styles.constructorLayout}>
        {/* Левая панель - Дерево объектов */}
        <div className={styles.leftPanel}>
          <div className={styles.panelHeader}>
            <span>Объекты</span>
            <button className={styles.addRootBtn} onClick={handleAddRoot}>
              <FaPlus size={12} />
              <span>Территория</span>
            </button>
          </div>
          <div className={styles.panelContent}>
            {treeLoading ? (
              <div className={styles.emptyState}>
                <FaSpinner className={styles.spinner} />
                <p>Загрузка...</p>
              </div>
            ) : objectTree.length === 0 ? (
              <div className={styles.emptyState}>
                <FaMap />
                <p>Добавьте территорию для начала работы</p>
              </div>
            ) : (
              <ObjectTree />
            )}
          </div>
        </div>

        {/* Центральная панель - Редактор схемы */}
        <div className={styles.centerPanel}>
          <SchemaEditor />
        </div>

        {/* Правая панель - Свойства */}
        <div className={styles.rightPanel}>
          <PropertiesPanel />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  objectTree: selectObjectTree(state),
  treeLoading: state.infoTerminal.treeLoading,
  hasChanges: state.infoTerminal.hasChanges,
  saving: state.infoTerminal.saving
});

export default connect(mapStateToProps)(InfoTerminal);
